import React, { useEffect, useRef, useState } from 'react';
import { rem } from '../../styles/rem';
import { DeclarationActionTypes } from '../../view-models/DeclarationActionTypes';
import InitiateDeclarationViewModel from '../InitiateDeclaration/types/InitiateDeclarationViewModel';
import { CollectionState, ElectionType } from '../../api/api-types';
import { SchemaFrontendUserDataViewModel } from '../../api/api-schema';
import { useAuth } from '../../api/AuthProvider';
import { usePartyDetailsUrl } from '../../application/PartyDetailsUrlContext';
import { StatusBarViewModel } from './types/StatusBarViewModel';
import { buildDialogUrl } from '../../utils/dialogFlowUrl';
import DeclarationDrawer from './DeclarationDrawer';
import StatusActionBar from './StatusActionBar';
import {
  getDaysLeftText,
  getElectionLabel,
  getStartDeclarationTargets,
  getStartEligibility,
  hasRightsWarning,
} from './statusBarHelpers';

type StatusBarProps = {
  model?: InitiateDeclarationViewModel;
  electionType?: ElectionType;
  collectionState: CollectionState;
  partyId?: string;
  partyName?: string | null;
  user?: SchemaFrontendUserDataViewModel;
  dialogPageUrl?: string;
  statusBarModel?: StatusBarViewModel;
  dkRunning?: boolean;
  euRunning?: boolean;
};

function StatusBar({
  electionType,
  partyId,
  partyName,
  collectionState,
  user,
  dialogPageUrl,
  statusBarModel,
  dkRunning,
  euRunning,
}: StatusBarProps) {
  const actionBarRef = useRef<HTMLDivElement | null>(null);
  const [hasMeasuredActionBar, setHasMeasuredActionBar] = useState(false);
  const [isCompactVisible, setIsCompactVisible] = useState(false);
  const { loginUrl } = useAuth();
  const { partyDetailsUrl } = usePartyDetailsUrl();
  const { canStartDeclaration, initiateElectionType } = getStartEligibility({
    collectionState,
    electionType,
    partyId,
    user,
    dkRunning,
    euRunning,
  });
  const shouldShowActionBar =
    !user || canStartDeclaration || hasRightsWarning(user);
  const startDeclarationUrl = !user
    ? loginUrl
    : buildDialogUrl(dialogPageUrl, {
        type: DeclarationActionTypes.initiate,
        electionType: initiateElectionType,
        party: partyId,
      });
  const startDeclarationTargets = getStartDeclarationTargets({
    dkRunning,
    euRunning,
    initiateElectionType,
    partyId,
    user,
  });

  useEffect(() => {
    if (!shouldShowActionBar) {
      setHasMeasuredActionBar(false);
      setIsCompactVisible(false);
      return;
    }

    const actionBarElement = actionBarRef.current;
    if (!actionBarElement) return;

    const getHeaderOffset = () =>
      window.matchMedia('(min-width: 48em)').matches ? 51 : 56;

    const updateVisibility = () => {
      if (actionBarElement.getClientRects().length === 0) {
        setHasMeasuredActionBar(false);
        setIsCompactVisible(false);
        return;
      }

      setHasMeasuredActionBar(true);
      const { bottom } = actionBarElement.getBoundingClientRect();
      setIsCompactVisible(bottom <= getHeaderOffset());
    };

    updateVisibility();

    const observer = new IntersectionObserver(updateVisibility, {
      threshold: 0,
    });

    observer.observe(actionBarElement);
    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', updateVisibility);
      window.removeEventListener('resize', updateVisibility);
    };
  }, [shouldShowActionBar]);

  return (
    <>
      {shouldShowActionBar && (
        <div ref={actionBarRef}>
          <StatusActionBar
            partyName={partyName}
            electionLabel={getElectionLabel(electionType, statusBarModel)}
            loginUrl={loginUrl}
            startDeclarationUrl={startDeclarationUrl}
            userVotingRights={user?.votingRights ?? undefined}
            isLoggedIn={!!user}
            canStartDeclaration={canStartDeclaration}
            statusBarModel={statusBarModel}
            startDeclarationTargets={startDeclarationTargets}
          />
        </div>
      )}
      {shouldShowActionBar && canStartDeclaration && hasMeasuredActionBar && (
        <div
          sx={{
            position: 'fixed',
            top: [rem(56), rem(51)],
            left: 0,
            right: 0,
            height: rem(60),
            overflow: 'hidden',
            pointerEvents: isCompactVisible ? 'auto' : 'none',
            zIndex: 1,
          }}
        >
          <div
            sx={{
              opacity: isCompactVisible ? 1 : 0,
              transform: isCompactVisible
                ? 'translateY(0)'
                : 'translateY(-100%)',
              transition:
                'opacity 180ms ease, transform 220ms cubic-bezier(0.2, 0, 0, 1)',
              '@media (prefers-reduced-motion: reduce)': {
                opacity: isCompactVisible ? 1 : 0,
                transition: 'none',
                transform: isCompactVisible
                  ? 'translateY(0)'
                  : 'translateY(-100%)',
              },
            }}
          >
            <StatusActionBar
              compact
              partyName={partyName}
              electionLabel={getElectionLabel(electionType, statusBarModel)}
              startDeclarationUrl={startDeclarationUrl}
              userVotingRights={user?.votingRights ?? undefined}
              isLoggedIn={!!user}
              canStartDeclaration={canStartDeclaration}
              statusBarModel={statusBarModel}
              startDeclarationTargets={startDeclarationTargets}
            />
          </div>
        </div>
      )}
      <DeclarationDrawer
        declarations={user?.userDeclarations ?? []}
        dialogPageUrl={dialogPageUrl}
        partyDetailsUrl={partyDetailsUrl}
        partyName={partyName}
        statusBarModel={statusBarModel}
      />
    </>
  );
}

export { getDaysLeftText };
export default StatusBar;
